import { Action, Thunk, action, thunk, State, ThunkOn, thunkOn } from 'easy-peasy';
import { VideoItem, VideoType, CocoaAPI } from '../typings';
import { StoreState } from './store';
import { VideoService } from '../services/VideoService';

export interface VideosState {
  data: CocoaAPI.Playback | null;
  workoutVideo: VideoItem | null;
  modifierVideo: VideoItem | null;
  setData: Action<VideosState, CocoaAPI.Playback | null>;
  setWorkoutVideo: Action<VideosState, VideoItem | null>;
  setModifierVideo: Action<VideosState, VideoItem | null>;
  reset: Action<VideosState>;
  fetchData: Thunk<VideosState, void, any, StoreState, Promise<VideosState>>;
  loadWorkoutVideo: Thunk<VideosState, void, any, StoreState>;
  closePlayer: Thunk<VideosState, void, any, StoreState>;
  switchVideo: Thunk<VideosState, VideoItem, any, StoreState>;
  onPlayerDestroy: ThunkOn<VideosState, any, StoreState>;
}

const initialState: State<VideosState> = {
  data: null,
  workoutVideo: null,
  modifierVideo: null,
};

export const videosState: VideosState = {
  ...initialState,

  /***********
   * Actions *
   ***********/
  setData: action((state, data) => {
    state.data = data;
  }),

  setWorkoutVideo: action((state, workoutVideo) => {
    state.workoutVideo = workoutVideo;
  }),

  setModifierVideo: action((state, modifierVideo) => {
    state.modifierVideo = modifierVideo;
  }),

  reset: action(state => {
    Object.assign(state, { ...initialState });
  }),

  /** Loads data from Video API and stores it state */
  fetchData: thunk(async (actions, _, { getState, getStoreState }) => {
    const { config } = getStoreState();

    const data = await VideoService.getPlaybackData({ config });

    actions.setWorkoutVideo({
      type: VideoType.WORKOUT,
      title: data.metadata.title,
      guid: data.metadata.guid || data.metadata.videoId,
      url: data.urls.playbackStandard,
      thumbnailUrl: data.thumbnail.image,
      resumePoint: data.metadata.resumePoint,
    });

    if (data.modifiers?.length) {
      actions.setModifierVideo({
        type: VideoType.MODIFIER,
        title: data.modifiers[0].title,
        guid: data.modifiers[0].guid || data.modifiers[0].videoId,
        url: data.modifiers[0].url,
        thumbnailUrl: data.thumbnail.image,
      });
    }

    actions.setData(data);

    return getState() as VideosState;
  }),

  /** Load Workout video */
  loadWorkoutVideo: thunk((actions, _, { getState, getStoreState, getStoreActions }) => {
    const state = getState();
    if (state.workoutVideo) {
      getStoreActions().player.loadVideo(state.workoutVideo);
    }
  }),

  /** Close the player */
  closePlayer: thunk((actions, _, { getState, getStoreState, getStoreActions }) => {
    const storeState = getStoreState();
    if (storeState.config.closeCallback) {
      storeState.config?.closeCallback();
    }
  }),

  /** Loads a different video, but resumes the current time */
  switchVideo: thunk((_, video, { getStoreActions, getStoreState }) => {
    const storeActions = getStoreActions();
    const { player } = getStoreState();

    storeActions.player.setPlaybackSwitchTime(player.playbackCurrentTime);
    storeActions.player.loadVideo(video);
  }),

  /*************
   * Listeners *
   *************/
  onPlayerDestroy: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.destroy];
    },
    function handler(actions) {
      actions.reset();
      VideoService.abortController.abort();
    }
  ),
};
